import { IF, NO, UIColor, UITextView, UIView } from "uicore-ts"
import { RHButton } from "./RHButton"
import { RHColor } from "./RHColor"
import { RHButtons, RHColors, RHFont, RHSpacing } from "./Utils_slop/RHDesignTokens"
import { RHTextSanitizer } from "./Utils_slop/RHTextSanitizer"
import { referenceId, RHReferenceLike } from "../../Core/RHReference"


export type RHTeamMemberDisplayData = {
    userId: string
    name: string
    email: string
}


export const RHProcurementTeamRoleDisplayLabels = {
    responsible: "Vastutav isik",
    authorized: "Volitatud isikud",
    observer: "Vaatlejad",
    evaluator: "Hindajad"
} as const


export const RHProcurementTeamMemberRoleDisplayLabels = {
    responsible: "Vastutav",
    authorized: "Volitatud",
    observer: "Vaatleja",
    evaluator: "Hindaja"
} as const


export class RHTeamUserReferenceResolver {
    
    static userIdFromReference(reference: RHReferenceLike): string {
        if (typeof reference === "object" && reference !== null && "userId" in reference && reference.userId) {
            return RHTeamUserReferenceResolver.userIdFromReference(reference.userId)
        }
        return referenceId(reference) ?? ""
    }
    
    
    static memberFromUserData(userData: {
        userId: string
        name: string
        email: string
    }): RHTeamMemberDisplayData {
        return {
            userId: userData.userId,
            name: userData.name,
            email: userData.email
        }
    }
    
    
    static memberFromReference(reference: RHReferenceLike): RHTeamMemberDisplayData {
        const contactInformation = RHTeamUserReferenceResolver._valueOfProperty(reference, "contactInformation")
        const contactName = RHTeamUserReferenceResolver._textOfProperty(contactInformation, "name")
        const contactEmail = RHTeamUserReferenceResolver._textOfProperty(contactInformation, "email")
        const email = RHTeamUserReferenceResolver._textOfProperty(reference, "email")
        
        const userId = RHTeamUserReferenceResolver.userIdFromReference(reference)
        let name = RHTeamUserReferenceResolver._textOfProperty(reference, "displayName") ??
            RHTeamUserReferenceResolver._textOfProperty(reference, "name") ?? contactName
        if (!name) {
            name = email ?? contactEmail ?? userId
        }
        
        return {
            userId,
            name,
            email: email ?? contactEmail ?? ""
        }
    }
    
    
    static _valueOfProperty(value: RHReferenceLike | number | boolean, propertyName: string): RHReferenceLike | number | boolean {
        if (typeof value === "object" && value !== null && propertyName in value) {
            return Reflect.get(value, propertyName)
        }
        return undefined
    }
    
    
    static _textOfProperty(value: RHReferenceLike | number | boolean, propertyName: string): string | undefined {
        const property = RHTeamUserReferenceResolver._valueOfProperty(value, propertyName)
        return typeof property === "string" ? property : undefined
    }
    
}


class RHTeamMemberRowView extends UIView {
    
    readonly nameLabel = new UITextView(undefined, UITextView.type.paragraph)
        .configuredWithObject({
            backgroundColor: UIColor.transparentColor,
            textColor: RHColors.textPrimary,
            isSingleLine: NO,
            style: {
                fontWeight: RHFont.weightMedium,
                overflowWrap: "anywhere",
                whiteSpace: "normal"
            }
        })
        .addedAsSubviewToView(this)
    
    readonly emailLabel = new UITextView(undefined, UITextView.type.paragraph)
        .configuredWithObject({
            backgroundColor: UIColor.transparentColor,
            textColor: RHColors.textMuted,
            isSingleLine: NO,
            style: {
                fontSize: RHFont.sizeSmall,
                overflowWrap: "anywhere",
                whiteSpace: "normal"
            }
        })
        .addedAsSubviewToView(this)
    
    constructor(elementID?: string) {
        super(elementID)
        this.configureWithObject({
            // @ts-ignore
            backgroundColor: UIColor.transparentColor,
            // @ts-ignore
            style: {
                borderBottom: "1px solid " + RHColors.borderLight.stringValue
            }
        })
    }
    
    configure(member: RHTeamMemberDisplayData) {
        this.nameLabel.text = RHTextSanitizer.escapedHTML(member.name)
        this.emailLabel.text = IF(member.email.length > 0)(() =>
            RHTextSanitizer.escapedHTML(member.email)
        ).ELSE(() =>
            "E-posti aadress puudub"
        )
    }
    
    override layoutSubviews() {
        super.layoutSubviews()
        const padding = RHSpacing.small
        const bounds = this.contentBoundsWithInset(padding)
        
        bounds
            .rectangleWithHeight(this.nameLabel)
            .assignedAsFrameOfView(this.nameLabel)
            .rectangleForNextRow(RHSpacing.tiny, this.emailLabel)
            .assignedAsFrameOfView(this.emailLabel)
    }
    
    override intrinsicContentHeight(constrainingWidth = 0): number {
        const padding = RHSpacing.small
        const contentWidth = Math.max(0, constrainingWidth - padding * 2)
        return padding * 2 +
            this.nameLabel.intrinsicContentHeight(contentWidth) +
            RHSpacing.tiny +
            this.emailLabel.intrinsicContentHeight(contentWidth)
    }
    
}


export class RHTeamRoleSectionView extends UIView {
    
    sectionContentPadding: number = RHSpacing.medium
    sectionItemSpacing: number = RHSpacing.small
    
    readonly titleLabel = new UITextView(undefined, UITextView.type.header4)
        .configuredWithObject({
            backgroundColor: UIColor.transparentColor,
            textColor: RHColors.textHeading,
            style: {
                fontSize: RHFont.sizeMedium,
                fontWeight: RHFont.weightSemibold
            }
        })
        .addedAsSubviewToView(this)
    
    readonly descriptionLabel = new UITextView(undefined, UITextView.type.paragraph)
        .configuredWithObject({
            backgroundColor: UIColor.transparentColor,
            textColor: RHColors.textMuted,
            isSingleLine: NO,
            style: {
                fontSize: RHFont.sizeSmall,
                overflowWrap: "anywhere",
                whiteSpace: "normal"
            }
        })
    
    readonly emptyLabel = new UITextView(undefined, UITextView.type.paragraph)
        .configuredWithObject({
            backgroundColor: UIColor.transparentColor,
            textColor: RHColor.textMuted,
            isSingleLine: NO,
            style: {
                fontSize: RHFont.sizeSmall,
                fontStyle: "italic",
                overflowWrap: "anywhere",
                whiteSpace: "normal"
            }
        })
        .addedAsSubviewToView(this)
    
    readonly actionButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: "Muuda rolli" },
            variant: RHButtons.compactPrimaryVariant,
            contentPadding: RHButtons.compactPrimaryContentPadding,
            style: {
                fontSize: RHButtons.compactPrimaryFontSize
            }
        })
    
    actionWasRequested?: () => void
    
    _memberRows: RHTeamMemberRowView[] = []
    _members: RHTeamMemberDisplayData[] = []
    _showsActionButton = NO
    _descriptionText = ""
    
    constructor(elementID?: string) {
        super(elementID)
        this.configureWithObject({
            // @ts-ignore
            backgroundColor: UIColor.whiteColor,
            // @ts-ignore
            style: {
                border: "1px solid " + RHColors.borderLight.stringValue,
                borderRadius: "8px"
            }
        })
        // noinspection JSConstantReassignment
        this.actionButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            this.actionWasRequested?.()
        }
    }
    
    configureSection(input: {
        title: string
        description?: string
        emptyText: string
        actionTitle?: string
        members: RHTeamMemberDisplayData[]
        showsActionButton: boolean
    }) {
        this.titleLabel.text = input.title
        this.emptyLabel.text = input.emptyText
        this._descriptionText = input.description ?? ""
        this._members = input.members
        this._showsActionButton = input.showsActionButton
        
        if (input.actionTitle) {
            this.actionButton.titleLabel.text = input.actionTitle
        }
        
        this._reloadRows()
        this.setNeedsLayoutUpToRootView()
    }
    
    _reloadRows() {
        for (const rowView of this._memberRows) {
            rowView.removeFromSuperview()
        }
        this._memberRows = []
        
        if (this._descriptionText.length > 0) {
            this.descriptionLabel.addedAsSubviewToView(this)
        }
        else {
            this.descriptionLabel.removeFromSuperview()
        }
        this.descriptionLabel.text = this._descriptionText
        
        if (this._members.length === 0) {
            this.emptyLabel.addedAsSubviewToView(this)
        }
        else {
            this.emptyLabel.removeFromSuperview()
            for (const member of this._members) {
                const rowView = new RHTeamMemberRowView()
                    .addedAsSubviewToView(this) as RHTeamMemberRowView
                rowView.configure(member)
                this._memberRows.push(rowView)
            }
        }
        
        if (this._showsActionButton) {
            this.actionButton.addedAsSubviewToView(this)
        }
        else {
            this.actionButton.removeFromSuperview()
        }
    }
    
    override layoutSubviews() {
        super.layoutSubviews()
        
        const padding = this.sectionContentPadding
        const bounds = this.contentBoundsWithInset(padding)
        const views = this._visibleFlowViews()
        
        bounds.framesByDistributingViewsAsColumn(
            views,
            this.sectionItemSpacing,
            this._flowViewHeights(views)
        )
        
        this._fitActionButtonIfVisible(bounds.width)
    }
    
    override intrinsicContentHeight(constrainingWidth = 0): number {
        const padding = this.sectionContentPadding
        const contentWidth = Math.max(0, constrainingWidth - padding * 2)
        const views = this._visibleFlowViews()
        let height = padding * 2
        
        for (const view of views) {
            if (view instanceof RHButton) {
                height += RHButtons.compactPrimaryHeight
            }
            else {
                height += view.intrinsicContentHeight(contentWidth)
            }
        }
        if (views.length > 1) {
            height += this.sectionItemSpacing * (views.length - 1)
        }
        return height
    }
    
    _visibleFlowViews(): UIView[] {
        const views: UIView[] = [this.titleLabel]
        if (this.descriptionLabel.superview === this) {
            views.push(this.descriptionLabel)
        }
        if (this.emptyLabel.superview === this) {
            views.push(this.emptyLabel)
        }
        else {
            views.push(...this._memberRows)
        }
        if (this.actionButton.superview === this) {
            views.push(this.actionButton)
        }
        return views
    }
    
    _flowViewHeights(views: UIView[]): (number | UIView)[] {
        const heights: (number | UIView)[] = []
        for (const view of views) {
            if (view instanceof RHButton) {
                heights.push(RHButtons.compactPrimaryHeight)
            }
            else {
                heights.push(view)
            }
        }
        return heights
    }
    
    _fitActionButtonIfVisible(availableWidth: number) {
        if (this.actionButton.superview !== this) {
            return
        }
        
        const buttonWidth = [
            [
                this.actionButton.titleLabel.intrinsicContentWidth(RHButtons.compactPrimaryHeight) +
                RHButtons.compactPrimaryContentPadding * 2 +
                RHSpacing.large,
                168
            ].max(),
            260,
            availableWidth
        ].min()
        
        this.actionButton.frame = this.actionButton.frame.rectangleWithWidth(buttonWidth, 0)
    }
    
}
